import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../common/FloatingTextInputComponent";
import {
  validateField,
  getFieldMax,
  getFieldMin,
} from "../../../../helper/fieldHelper";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import { sendWorkflowManager } from "../sendWorkflow/builder/workflowManager";
import { requiresAdditionalInfo } from "../helper/transactionFieldsHelper";
import type { FieldInfo } from "../models/fieldInfo";

export function ReceiverInfoScreen() {
  const { t } = useTranslation();
  const fieldInfo =
    useJourneyStore(
      (state) => state.journeyData.transactionFieldsSendResponse?.fieldInfo
    ) ?? [];
  const receiverInfo = useJourneyStore((state) => state.journeyData.receiverInfo);
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const { goNext } = useWorkflowNavigation();

  const receiverNameFields = useMemo(() => {
    return fieldInfo
      .filter((f: FieldInfo) => f.field.startsWith("receiver.name"))
      .sort(
        (a, b) =>
          Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
      );
  }, [fieldInfo]);

  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      receiverNameFields.forEach((f) => {
        if (next[f.field] !== undefined) return;
        const part = f.field.replace("receiver.name.", "");
        if (part === "firstName") next[f.field] = receiverInfo.firstName;
        else if (part === "middleName") next[f.field] = receiverInfo.middleName ?? "";
        else if (part === "lastName") next[f.field] = receiverInfo.lastName;
        else if (part === "secondLastName") next[f.field] = receiverInfo.secondLastName ?? "";
        else next[f.field] = "";
      });
      return next;
    });
  }, [receiverNameFields, receiverInfo]);

  const getLabel = (field: FieldInfo) => {
    const label = field.fieldLabel || field.field;
    if (field.required) return label;
    return `${label} (optional)`;
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    receiverNameFields.forEach((f) => {
      newErrors[f.field] = validateField(
        fieldInfo,
        f.field,
        values[f.field] ?? ""
      );
    });
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      return;
    }

    updateJourneyData({
      receiverInfo: {
        firstName: values["receiver.name.firstName"] ?? receiverInfo.firstName,
        middleName: values["receiver.name.middleName"] ?? receiverInfo.middleName,
        lastName: values["receiver.name.lastName"] ?? receiverInfo.lastName,
        secondLastName:
          values["receiver.name.secondLastName"] ?? receiverInfo.secondLastName,
      },
    });

    // Dump any required fields the app did not collect into the Additional Info step.
    sendWorkflowManager.updateContext({
      requiresAdditionalInfo: requiresAdditionalInfo(
        useJourneyStore.getState().journeyData
      ),
    });
    goNext();
  };

  useStepAction(handleNext);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID="receiver-info-screen"
    >
      <Text style={styles.title}>
        {t("RECEIVER_DETAILS_TITLE") || "Receiver Information"}
      </Text>
      <Text style={styles.subtitle}>
        {t("RECEIVER_DETAILS_SUBTITLE") ||
          "Please enter the receiver's name details."}
      </Text>

      {receiverNameFields.map((field, index) => {
        const max = getFieldMax(fieldInfo, field.field);
        const min = getFieldMin(fieldInfo, field.field);
        return (
          <View key={field.field}>
            <FloatingTextInputComponent
              testID={`sendMoney-${field.field}`}
              label={getLabel(field)}
              value={values[field.field] ?? ""}
              isAutoFocus={index === 0}
              maxLength={max ? Number(max) : undefined}
              minLength={min ? Number(min) : undefined}
              onChangeText={(val) => {
                setValues((prev) => ({ ...prev, [field.field]: val }));
                if (errors[field.field]) {
                  setErrors((prev) => ({ ...prev, [field.field]: "" }));
                }
              }}
              errorText={errors[field.field]}
              blockPersonNameAutofill
            />
            {field.required && field.helpTextLong ? (
              <Text style={styles.helpText}>{field.helpTextLong}</Text>
            ) : null}
          </View>
        );
      })}

    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  helpText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    color: Colors.subTitleColor,
    lineHeight: 16,
    marginTop: 4,
    marginBottom: 8,
  },
});
